import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import api from '../../services/api';
import { confirmAction } from '../../utils/confirm';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#3949ab' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 35, 126, 0.1)' : 'none',
    '&:hover': { borderColor: '#3949ab' },
    fontSize: 14,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#1a237e' : state.isFocused ? '#e8eaf6' : 'white',
  }),
  placeholder: (base) => ({ ...base, color: '#9e9e9e' }),
};

function MemberModal({ tenantId, bosId, academicYearId, member, users, designations, onClose, onSaved, onDesignationCreated }) {
  const isEdit = !!member;
  const [selectedUser, setSelectedUser] = useState(
    member ? { value: member.UserID, label: `${member.FullName} (${member.Email})` } : null
  );
  const [selectedDesig, setSelectedDesig] = useState(
    member?.DesignationID ? { value: member.DesignationID, label: member.DesignationName } : null
  );
  const [isChairman, setIsChairman] = useState(member?.IsChairman || false);
  const [saving, setSaving] = useState(false);
  const [creatingDesig, setCreatingDesig] = useState(false);

  const userOptions = useMemo(() =>
    users.map(u => ({ value: u.UserID, label: `${u.FullName} (${u.Email})` })),
    [users]
  );

  const desigOptions = useMemo(() =>
    designations.map(d => ({ value: d.DesignationID, label: d.DesignationName })),
    [designations]
  );

  const handleCreateDesignation = async (inputValue) => {
    setCreatingDesig(true);
    try {
      const res = await api.post('/bos/designations', { tenantId, designationName: inputValue.trim() });
      if (res.data.success) {
        const newOption = { value: res.data.data.designationId, label: res.data.data.designationName };
        setSelectedDesig(newOption);
        onDesignationCreated(res.data.data);
        toast.success(`Designation "${inputValue.trim()}" created`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create designation');
    } finally {
      setCreatingDesig(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return toast.error('Please select a user');

    setSaving(true);
    try {
      const payload = {
        tenantId,
        bosId,
        academicYearId,
        userId: selectedUser.value,
        designationId: selectedDesig?.value || null,
        isChairman,
      };

      if (isEdit) {
        await api.put(`/bos/members/${member.MemberID}`, payload);
        toast.success('Member updated');
      } else {
        await api.post('/bos/members', payload);
        toast.success('Member added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Member' : 'Add BOS Member'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">User</label>
            <Select
              options={userOptions}
              value={selectedUser}
              onChange={setSelectedUser}
              placeholder="Search user by name or email..."
              isClearable
              isSearchable
              isDisabled={isEdit}
              styles={selectStyles}
              noOptionsMessage={() => 'No users found'}
            />
          </div>
          <div className="form-group">
            <label>Designation</label>
            <CreatableSelect
              options={desigOptions}
              value={selectedDesig}
              onChange={setSelectedDesig}
              onCreateOption={handleCreateDesignation}
              placeholder="Search or type to create new..."
              isClearable
              isSearchable
              isLoading={creatingDesig}
              isDisabled={creatingDesig}
              styles={selectStyles}
              formatCreateLabel={(input) => `Create "${input}"`}
              noOptionsMessage={() => 'Type to create a new designation'}
            />
            <span className="form-hint">Type a new designation name and press Enter to create it</span>
          </div>
          <div className="form-group" style={{ marginTop: 8 }}>
            <div className="chairman-toggle-row">
              <label className="chairman-label">BOS Chairman</label>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isChairman}
                  onChange={(e) => setIsChairman(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              {isChairman && <span className="chairman-badge">Chairman</span>}
            </div>
            <span className="form-hint">Only one chairman per BOS per academic year. Enabling this will remove chairman status from any other member.</span>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Add Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BOSMembersTab({ tenantId }) {
  const [academicYears, setAcademicYears] = useState([]);
  const [bosList, setBOSList] = useState([]);
  const [users, setUsers] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedBOS, setSelectedBOS] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMember, setEditMember] = useState(null);

  // Load dropdowns
  useEffect(() => {
    if (!tenantId) return;
    async function loadDropdowns() {
      try {
        const [yearRes, bosRes, userRes, desigRes] = await Promise.all([
          api.get(`/academic/years/${tenantId}`),
          api.get(`/bos/dropdown/${tenantId}`),
          api.get(`/bos/users/${tenantId}`),
          api.get(`/bos/designations/${tenantId}`),
        ]);
        if (yearRes.data.success) setAcademicYears(yearRes.data.data);
        if (bosRes.data.success) setBOSList(bosRes.data.data);
        if (userRes.data.success) setUsers(userRes.data.data);
        if (desigRes.data.success) setDesignations(desigRes.data.data);
      } catch {
        toast.error('Failed to load data');
      } finally {
        setInitialLoading(false);
      }
    }
    loadDropdowns();
  }, [tenantId]);

  const yearOptions = useMemo(() =>
    academicYears.map(y => ({ value: y.AcademicYearID, label: y.YearName })),
    [academicYears]
  );

  const bosOptions = useMemo(() =>
    bosList.map(b => ({ value: b.BOSID, label: b.BOSName })),
    [bosList]
  );

  // Load members when BOS + Year selected
  const loadMembers = useCallback(async () => {
    if (!selectedBOS || !selectedYear) { setMembers([]); return; }
    try {
      setLoading(true);
      const res = await api.get(`/bos/members/${selectedBOS.value}/${selectedYear.value}`);
      if (res.data.success) setMembers(res.data.data);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [selectedBOS, selectedYear]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleRemove = async (memberId, memberName) => {
    const confirmed = await confirmAction(
      'Remove Member?',
      `Remove "${memberName}" from this BOS?`,
      { confirmText: 'Yes, remove', confirmColor: '#c62828', icon: 'warning' }
    );
    if (!confirmed) return;
    try {
      await api.delete(`/bos/members/${memberId}`);
      toast.success('Member removed');
      loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove');
    }
  };

  const handleDesignationCreated = (data) => {
    setDesignations(prev => [...prev, { DesignationID: data.designationId, DesignationName: data.designationName, IsActive: true }]);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '??';
  };

  const canAddMember = selectedYear && selectedBOS;

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>BOS Members</h3>
        <button
          className="btn btn-add"
          onClick={() => { setEditMember(null); setShowModal(true); }}
          disabled={!canAddMember}
          title={!canAddMember ? 'Select Academic Year and BOS first' : ''}
        >
          + Add Member
        </button>
      </div>

      {/* Selection toolbar */}
      <div className="bos-member-selectors">
        <div className="bos-member-select-group">
          <label>Academic Year</label>
          <Select
            options={yearOptions}
            value={selectedYear}
            onChange={(val) => { setSelectedYear(val); }}
            placeholder="Select academic year..."
            isClearable
            isSearchable
            isLoading={initialLoading}
            styles={{ ...selectStyles, control: (base, state) => ({ ...selectStyles.control(base, state), minWidth: 220 }) }}
            noOptionsMessage={() => 'No academic years found'}
          />
        </div>
        <div className="bos-member-select-group">
          <label>Board of Studies</label>
          <Select
            options={bosOptions}
            value={selectedBOS}
            onChange={(val) => { setSelectedBOS(val); }}
            placeholder="Select BOS..."
            isClearable
            isSearchable
            isLoading={initialLoading}
            styles={{ ...selectStyles, control: (base, state) => ({ ...selectStyles.control(base, state), minWidth: 250 }) }}
            noOptionsMessage={() => 'No BOS found'}
          />
        </div>
      </div>

      {!canAddMember ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          Select an Academic Year and BOS to view and manage members.
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : members.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No members added yet. Click "Add Member" to get started.
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Member</th>
              <th>Email</th>
              <th>Designation</th>
              <th>Role</th>
              <th>Added By</th>
              <th>Added On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.MemberID}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="table-avatar">{getInitials(m.FullName)}</div>
                    <div>
                      <strong>{m.FullName}</strong>
                      {m.IsChairman ? <span className="chairman-badge" style={{ marginLeft: 8 }}>Chairman</span> : null}
                    </div>
                  </div>
                </td>
                <td style={{ color: '#616161', fontSize: 13 }}>{m.Email}</td>
                <td>
                  {m.DesignationName ? (
                    <span className="badge badge-provider">{m.DesignationName}</span>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </td>
                <td>
                  {m.IsChairman ? (
                    <span className="badge badge-chairman">BOS Chairman</span>
                  ) : (
                    <span className="badge badge-member">Member</span>
                  )}
                </td>
                <td style={{ color: '#616161', fontSize: 13 }}>{m.AddedByName || '-'}</td>
                <td style={{ color: '#616161', fontSize: 13 }}>{formatDate(m.AddedAt)}</td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn action-edit" title="Edit" onClick={() => { setEditMember(m); setShowModal(true); }}>✏</button>
                    <button className="action-btn action-delete" title="Remove" onClick={() => handleRemove(m.MemberID, m.FullName)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Member count summary */}
      {canAddMember && members.length > 0 && (
        <div className="bos-member-summary">
          <span>{members.length} member(s)</span>
          {members.some(m => m.IsChairman) && (
            <span> &middot; Chairman: <strong>{members.find(m => m.IsChairman)?.FullName}</strong></span>
          )}
        </div>
      )}

      {showModal && canAddMember && (
        <MemberModal
          tenantId={tenantId}
          bosId={selectedBOS.value}
          academicYearId={selectedYear.value}
          member={editMember}
          users={users}
          designations={designations}
          onClose={() => { setShowModal(false); setEditMember(null); }}
          onSaved={() => { setShowModal(false); setEditMember(null); loadMembers(); }}
          onDesignationCreated={handleDesignationCreated}
        />
      )}
    </>
  );
}

export default BOSMembersTab;
